import { Fragment, useEffect, useMemo } from "react";
import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { api } from "@/api/client";
import { setWorkspace, useWorkspace } from "@/api/workspace";
import type { Workspace } from "@/api/workspace";
import { onboardingApi } from "@/features/onboarding/api";
import type { WorkspaceContext as WorkspaceContextData } from "@/features/onboarding/api";
import { MkLayout } from "@/mk/MkLayout";
import { PmGlobalSearch } from "@/pm/PmGlobalSearch";
import { PmWorkspaceToggle } from "@/pm/PmWorkspaceToggle";

import { useAuth } from "./auth";
import { activeFor, navTree, navigationQuery, recordRoute, routeFor, sectionAllowed,
         trailFor } from "./navigation";
import type { NavAccess } from "./navigation";

export type WorkspaceContext = WorkspaceContextData;

export function useNavAccess() {
    return useQuery<{ data: NavAccess }>({
        queryKey: ["navigation"],
        queryFn: () => api.raw<NavAccess>(navigationQuery()),
        staleTime: 5 * 60 * 1000,
    });
}

const KEPT_QUERIES = new Set(["navigation", "workspace"]);

export function useWorkspaceContext() {
    return useQuery<WorkspaceContext>({
        queryKey: ["workspace", "context"],
        queryFn: () => onboardingApi.context(),
    });
}

export function Shell({ children }: { children: ReactNode }) {
    const { t } = useTranslation();
    const { user, signOut } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const access = useNavAccess();
    const context = useWorkspaceContext();
    const queryClient = useQueryClient();
    const workspace = useWorkspace();

    const isHome = location.pathname === "/";
    const active = isHome ? undefined : activeFor(
        location.pathname, location.search,
        (_app, section) => sectionAllowed(access.data?.data, section,
                                          context.data?.is_owner_org),
        workspace);
    const activeApp = active?.split(":")[0];

    const items = useMemo(
        () => (access.data
            ? navTree(t, access.data.data, activeApp, context.data?.is_owner_org, workspace)
            : []),
        [t, access.data, activeApp, context.data?.is_owner_org, workspace]);

    const trail = useMemo(() => trailFor(t, active), [t, active, workspace]);

    function applyWorkspace(next: Workspace) {
        setWorkspace(next);
        queryClient.removeQueries({
            predicate: (query) => !KEPT_QUERIES.has(String(query.queryKey[0])),
        });
    }

    const routeType = location.pathname === "/projects"
        ? new URLSearchParams(location.search).get("type") : null;

    useEffect(() => {
        if ((routeType === "design" || routeType === "execution") && routeType !== workspace) {
            applyWorkspace(routeType);
        }
    }, [routeType]);

    function switchWorkspace(next: Workspace) {
        if (next === workspace) {
            return;
        }
        applyWorkspace(next);
        if (location.pathname === "/projects") {
            navigate(`/projects?type=${next}`);
        } else if (!isHome && !activeFor(location.pathname, location.search, undefined, next)) {
            navigate("/");
        }
    }

    return (
        <div className={`pm-app${isHome ? " pm-home-app" : ""}`}>
            <MkLayout
                sidebarSection={activeApp ?? "home"}
                brand={{ label: t("nav.home"), icon: "fa-home", onClick: () => navigate("/") }}
                personal={{
                    name: user?.name ?? "",
                    status: context.data?.presence ?? "offline",
                    avatarUrl: user ? `/api/v1/files/image/res.users/${user.id}/avatar_128` : null,
                }}
                items={items}
                active={active}
                trail={trail}
                breadcrumb={!isHome}
                sidebarExtra={<PmWorkspaceToggle value={workspace} onChange={switchWorkspace} />}
                header={<PmGlobalSearch onOpenRecord={(model, id) => {
                    const route = recordRoute(model, id);
                    if (route) {
                        navigate(route);
                    }
                }} />}
                onSelect={(id) => {
                    if (id.startsWith("crumb:")) {
                        return;
                    }
                    const route = routeFor(id);
                    if (route) {
                        navigate(route);
                    }
                }}
                onOpenRecord={(model, id) => {
                    const route = recordRoute(model, id);
                    if (route) {
                        navigate(route);
                    }
                }}
                onLogout={() => void signOut()}
            >
                {isHome ? <Fragment key={workspace}>{children}</Fragment> : (
                    <div className="pm-page-swap" key={`${workspace}:${active ?? location.pathname}`}>
                        {children}
                    </div>
                )}
            </MkLayout>
        </div>
    );
}
